import React, { useState, useEffect, createContext, useContext } from 'react';

/**
 * TECH DEBT (Fluent 2 Migration)
 * This responsive system should be replaced during Fluent 2 migration:
 * - Breakpoints are duplicated here and in analysis/styles/index.css
 * - Components mix JS conditional rendering with CSS media queries inconsistently
 * - Some components render duplicate DOM nodes (mobile + desktop) hidden via CSS
 * - Heavy use of !important in CSS to override inline styles
 * Replace with Fluent 2's makeStyles breakpoints and consider CSS Container Queries.
 */

// Centralized breakpoint constants matching standard Bootstrap 4 breakpoints where possible
// mobile < 768px (md)
// tablet >= 768px && < 1024px (custom tablet size, close to lg 992px)
// compact <= 1450px
export const BREAKPOINTS = {
    MOBILE: 768,
    TABLET: 1024,
    COMPACT: 1570,
} as const;

export const CHART_MARGINS = {
    LEFT_MOBILE: 45,
    LEFT_DESKTOP: 70,
} as const;

export const LAYOUT = {
    SIDEBAR_MAX_WIDTH: 350,
    SIDEBAR_TABLET_WIDTH: 280,
    COMPONENT_HEIGHT: 'calc(100vh - 80px)',
    MOBILE_COMPONENT_HEIGHT: 'auto',
} as const;

interface ResponsiveState {
    isMobile: boolean;
    isTablet: boolean;
    isCompact: boolean;
    isDesktop: boolean;
}

const initialState: ResponsiveState = {
    isMobile: false,
    isTablet: false,
    isCompact: false,
    isDesktop: true,
};

const ResponsiveContext = createContext<ResponsiveState>(initialState);

export const ResponsiveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<ResponsiveState>(() => {
        if (globalThis.window === undefined) {
            return initialState;
        }
        const width = globalThis.window.innerWidth;
        const isMobile = width < BREAKPOINTS.MOBILE;
        const isTablet = width >= BREAKPOINTS.MOBILE && width < BREAKPOINTS.TABLET;
        return {
            isMobile,
            isTablet,
            isCompact: width <= BREAKPOINTS.COMPACT,
            isDesktop: !isMobile && !isTablet,
        };
    });

    useEffect(() => {
        if (globalThis.window === undefined) return;

        // Media query lists
        const mobileQuery = globalThis.window.matchMedia(`(max-width: ${BREAKPOINTS.MOBILE - 1}px)`);
        const tabletQuery = globalThis.window.matchMedia(
            `(min-width: ${BREAKPOINTS.MOBILE}px) and (max-width: ${BREAKPOINTS.TABLET - 1}px)`,
        );
        const compactQuery = globalThis.window.matchMedia(`(max-width: ${BREAKPOINTS.COMPACT}px)`);

        const handleChange = () => {
            const isMobile = mobileQuery.matches;
            const isTablet = tabletQuery.matches;
            setState({
                isMobile,
                isTablet,
                isCompact: compactQuery.matches,
                isDesktop: !isMobile && !isTablet,
            });
        };

        // Initial check is handled by lazy state, but matchMedia might be more accurate for scrollbar handling
        handleChange();

        mobileQuery.addEventListener('change', handleChange);
        tabletQuery.addEventListener('change', handleChange);
        compactQuery.addEventListener('change', handleChange);

        return () => {
            mobileQuery.removeEventListener('change', handleChange);
            tabletQuery.removeEventListener('change', handleChange);
            compactQuery.removeEventListener('change', handleChange);
        };
    }, []);

    return <ResponsiveContext.Provider value={state}>{children}</ResponsiveContext.Provider>;
};

export const useResponsive = () => useContext(ResponsiveContext);

export const responsiveHOC = (Component: React.ComponentType<any>) => {
    return function WrappedComponent(props: any) {
        const { isMobile, isTablet, isCompact, isDesktop } = useResponsive();

        return (
            <Component {...props} isMobile={isMobile} isTablet={isTablet} isCompact={isCompact} isDesktop={isDesktop} />
        );
    };
};
